import React from "react";
import { Root as CheckboxRoot, Indicator as CheckboxIndicator } from "@radix-ui/react-checkbox";
import { CheckIcon } from "@radix-ui/react-icons";
import { Check, Pencil, X } from "lucide-react";
import { cn } from "@/utils/cn";

interface GoalSelectionCardProps {
  id: string;
  title: string;
  category?: string;
  detail?: string;
  isSelected: boolean;
  isDisabled?: boolean;
  isEditing?: boolean;
  editingText?: string;
  editingDetail?: string;
  onToggle: () => void;
  onEditStart?: () => void;
  onEditSave?: () => void;
  onEditCancel?: () => void;
  onEditChange?: (value: string) => void;
  onEditDetailChange?: (value: string) => void;
  onKeyDown?: (e: React.KeyboardEvent<HTMLInputElement>) => void;
}

export const GoalSelectionCard: React.FC<GoalSelectionCardProps> = ({
  id,
  title,
  category,
  detail,
  isSelected,
  isDisabled,
  isEditing,
  editingText,
  editingDetail,
  onToggle,
  onEditStart,
  onEditSave,
  onEditCancel,
  onEditChange,
  onEditDetailChange,
  onKeyDown,
}) => {
  const handleCardClick = (e: React.MouseEvent) => {
    if (
      e.target instanceof HTMLButtonElement ||
      e.target instanceof HTMLInputElement ||
      e.target instanceof HTMLTextAreaElement ||
      isDisabled ||
      isEditing
    )
      return;
    onToggle();
  };

  return (
    <div
      onClick={handleCardClick}
      className={cn(
        "flex items-start gap-4 p-6 rounded-xl border-2 transition-colors",
        isSelected
          ? "bg-goal-selected-card border-transparent"
          : isDisabled
            ? "bg-secondary-50 dark:bg-white/5 border-secondary-200 dark:border-white/10 opacity-50 cursor-not-allowed"
            : "bg-secondary-50 dark:bg-white/5 border-secondary-200 dark:border-white/10 hover:border-primary-350 cursor-pointer",
      )}
    >
      {/* Checkbox */}
      <CheckboxRoot
        id={id}
        checked={isSelected}
        onCheckedChange={() => !isDisabled && !isEditing && onToggle()}
        className={cn(
          "relative flex-shrink-0 mt-1 w-6 h-6 rounded-[4px] border-2 flex items-center justify-center focus:outline-none focus:ring-2 focus:ring-primary-350 focus:ring-offset-1",
          isSelected
            ? "border-white bg-white data-[state=checked]:bg-white"
            : "border-secondary-200 dark:border-white/20 bg-white dark:bg-white/10 data-[state=checked]:border-transparent data-[state=checked]:bg-checkbox-gradient",
        )}
      >
        <CheckboxIndicator>
          <CheckIcon className={cn("w-[14px] h-[14px]", isSelected ? "text-primary-purple" : "text-white")} />
        </CheckboxIndicator>
      </CheckboxRoot>

      {/* Content */}
      <div className="flex-1 min-w-0">
        {/* Top row: category badge + edit button */}
        <div className="flex items-start justify-between mb-2">
          {category && (
            <span
              className={cn(
                "inline-flex items-center rounded-full px-3 py-1 text-xs font-medium",
                isSelected
                  ? "bg-white/20 text-white"
                  : "bg-ai-goal-category-bg text-ai-goal-category-text",
              )}
            >
              {category}
            </span>
          )}
          {onEditStart && !isEditing && (
            <button
              onClick={(e) => {
                e.stopPropagation();
                onEditStart();
              }}
              className={cn(
                "p-1.5 rounded-md transition-colors flex-shrink-0",
                isSelected
                  ? "text-white/80 hover:text-white hover:bg-white/10"
                  : "text-accent-250 dark:text-pab-dark-subtle-text hover:text-secondary-900 dark:hover:text-white hover:bg-secondary-100 dark:hover:bg-white/10",
              )}
              title="Edit"
            >
              <Pencil size={14} />
            </button>
          )}
        </div>

        {/* Title */}
        {isEditing ? (
          <div className="flex items-center gap-2">
            <input
              type="text"
              value={editingText}
              onChange={(e) => onEditChange?.(e.target.value)}
              onKeyDown={onKeyDown}
              className="flex-1 min-w-0 text-base text-secondary-900 dark:text-white bg-white dark:bg-white/5 border border-secondary-200 dark:border-white/10 rounded-lg px-3 py-2.5 focus:outline-none focus:ring-2 focus:ring-primary-350 focus:border-primary-350"
              autoFocus
            />
            <button
              onClick={(e) => {
                e.stopPropagation();
                onEditSave?.();
              }}
              className="flex-shrink-0 p-2 rounded-lg bg-ai-goal-category-bg text-ai-goal-category-text hover:opacity-80 transition-opacity"
              title="Save"
            >
              <Check size={16} />
            </button>
            <button
              onClick={(e) => {
                e.stopPropagation();
                onEditCancel?.();
              }}
              className="flex-shrink-0 p-2 rounded-lg text-accent-250 dark:text-pab-dark-subtle-text hover:text-secondary-900 dark:hover:text-white hover:bg-secondary-100 dark:hover:bg-white/10 transition-colors"
              title="Cancel"
            >
              <X size={16} />
            </button>
          </div>
        ) : (
          <h3
            className={cn(
              "text-base font-semibold leading-snug",
              isSelected ? "text-white" : "text-secondary-900 dark:text-white",
            )}
          >
            {title}
          </h3>
        )}

        {/* Detail / Result */}
        {isEditing ? (
          <div className="mt-3">
            <p className="mb-1 text-xs font-semibold text-accent-250 dark:text-pab-dark-subtle-text">
              Result
            </p>
            <textarea
              value={editingDetail}
              onChange={(e) => onEditDetailChange?.(e.target.value)}
              rows={2}
              className="w-full text-base text-secondary-900 dark:text-white bg-white dark:bg-white/5 border border-secondary-200 dark:border-white/10 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary-350 focus:border-primary-350 resize-none"
            />
          </div>
        ) : (
          detail && (
            <p
              className={cn(
                "mt-1.5 text-sm leading-5",
                isSelected ? "text-white/90" : "text-accent-250 dark:text-pab-dark-subtle-text",
              )}
            >
              <span className={cn("font-semibold", isSelected ? "text-white" : "text-secondary-900 dark:text-white")}>
                Result:{" "}
              </span>
              {detail}
            </p>
          )
        )}
      </div>
    </div>
  );
};

export default GoalSelectionCard;
